import classNames from "classnames"
import ResizeSensor from "css-element-queries/src/ResizeSensor"
import PerfectScrollbar from "perfect-scrollbar"
import PropTypes from "prop-types"
import { equals, isNil } from "ramda"
import React, { Component } from "react"

import { getButton } from "./buttonTypes.es6"
import styles from "./index.less"
import { updatePosition } from "./updatePosition.es6"

class DropDownMenu extends Component {
  perfectScrollbar = null

  state = {
    currentItem: null,
    hidePerfectScrollbar: false,
    itemList: null,
    menuMinWidth: undefined,
    menuOffsetLeft: undefined,
    menuOffsetTop: undefined,
    showMenu: false,
    syncWidth: undefined,
  }

  componentDidMount() {
    window.addEventListener("touchstart", this.onClickAway)
    window.addEventListener("click", this.onClickAway)

    if (this.props.doDetectResize) {
      this.attachResizeSensor()
    }

    window.addEventListener("scroll", this.adjustOpenMenuPosition, true)
    window.addEventListener("resize", this.adjustOpenMenuPosition)

    this.adjustMenuPosition()

    if (this._menu == null) {
      return
    }

    this.perfectScrollbar = new PerfectScrollbar(this._menu, this.getScrollbarOptions())
  }

  setInitialItemList = () => {
    if (!this.props.children || typeof this.props.children.map !== "function") return null
    const newList = this.props.children.map(({ key }) => key)

    if (!equals(this.state.itemList, newList)) this.setState({ itemList: newList })
  }

  componentDidUpdate() {
    this.adjustMenuPosition()

    this.setInitialItemList()

    if (this._menu == null || this.props.hidePerfectScrollbar) {
      return
    }

    if (this.perfectScrollbar) {
      this.perfectScrollbar.update()
    } else {
      this.perfectScrollbar = new PerfectScrollbar(this._menu, this.getScrollbarOptions())
    }
  }

  componentWillUnmount() {
    window.removeEventListener("touchstart", this.onClickAway)
    window.removeEventListener("click", this.onClickAway)

    window.removeEventListener("scroll", this.adjustOpenMenuPosition, true)
    window.removeEventListener("resize", this.adjustOpenMenuPosition)

    this.detachResizeSensor()

    if (this.perfectScrollbar && this._menu != null) {
      this.perfectScrollbar.destroy()
    }

    this.perfectScrollbar = null
  }

  getScrollbarOptions() {
    return {
      wheelPropagation: false,
    }
  }

  handleShowMenu = (newState = null) => {
    if (!isNil(newState)) {
      this.setState({ showMenu: newState })
      return
    }

    if (this.state.showMenu) {
      this.setState({ showMenu: false })
      return
    }

    this.setState({ showMenu: true })
  }

  attachResizeSensor() {
    if (this._wrap == null || this._menu == null) {
      return
    }

    if (this._wrapResizeSensor == null) {
      this._wrapResizeSensor = new ResizeSensor(this._wrap, this.adjustMenuPosition)
    }

    if (this._menuResizeSensor == null) {
      this._menuResizeSensor = new ResizeSensor(this._menu, this.adjustMenuPosition)
    }
  }
  adjustMenuPosition() {
    updatePosition({
      _menu: this._menu,
      _wrap: this._wrap,
      props: this.props,
      state: this.state,
      updateState: (value) => {
        this.setState(value)
      },
    })
  }
  detachResizeSensor() {
    if (this._wrapResizeSensor != null) {
      this._wrapResizeSensor.detach()
    }

    if (this._menuResizeSensor != null) {
      this._menuResizeSensor.detach()
    }
  }
  adjustOpenMenuPosition = () => {
    if (!this.state.showMenu) {
      return
    }

    this.adjustMenuPosition()
  }
  onClickAway = (event) => {
    if (event.defaultPrevented || !this.state.showMenu) {
      return
    }

    if (
      (event.target !== this._wrap && event.target === window) ||
      (document.documentElement.contains(event.target) &&
        !this.isDescendant(this._wrap, event.target))
    ) {
      this.setState({ showMenu: false })
    }
  }
  isDescendant(parent, child) {
    let node = child.parentNode

    while (node !== null) {
      if (node === parent) return true
      node = node.parentNode
    }

    return false
  }
  _setWrap = (reference) => (this._wrap = reference)
  _setMenu = (reference) => {
    if (this.perfectScrollbar && this._menu != null) {
      this.perfectScrollbar.destroy()
    }

    this.perfectScrollbar = null
    this._menu = reference
  }

  setCurrentItem = (item) => {
    if (this.props && this.props.listId) {
      const { listId } = this.props

      if (this._wrap) {
        this._wrap.setAttribute("aria-labelledby", `${listId}${item}`)
        this._wrap.setAttribute("aria-activedescendant", `${listId}${item}`)
      }

      this.setState({
        currentItem: item,
      })
    }
  }

  pickPrevItem = () => {
    if (!Array.isArray(this.state.itemList)) return

    const { currentItem } = this.state

    if (isNil(currentItem)) {
      this.setCurrentItem(this.state.itemList[0])
      return
    }

    const currentItemIndex = this.state.itemList.indexOf(currentItem)

    this.setCurrentItem(this.state.itemList[currentItemIndex - 1] || currentItem)
  }

  pickNextItem() {
    if (!Array.isArray(this.state.itemList)) return

    const { currentItem } = this.state

    if (isNil(currentItem)) {
      this.setCurrentItem(this.state.itemList[0])
      return
    }

    const currentItemIndex = this.state.itemList.indexOf(currentItem)

    this.setCurrentItem(this.state.itemList[currentItemIndex + 1] || currentItem)
  }

  arrowHandler = (keyCode) => {
    if (keyCode === 38) this.pickPrevItem()
    if (keyCode === 40) this.pickNextItem()
  }

  itemReference = React.createRef()

  enterHandler = () => {
    const itemToEvent = document.querySelector(`#${this.props.listId}${this.state.currentItem}`)

    if (itemToEvent) itemToEvent.click()
    this.onBlurHandler()
  }

  onBlurHandler = () => {
    if (this._wrap) {
      this._wrap.setAttribute("aria-labelledby", this.props.listId)
      this._wrap.removeAttribute("aria-activedescendant")
    }
  }

  render() {
    const { currentItem, menuMinWidth, menuOffsetLeft, menuOffsetTop, showMenu, syncWidth } =
      this.state
    const {
      children,
      doAlignMenu,
      doAlignMenuTop,
      doSyncMinWidth,

      doSyncWidth,
      hasSubMenu,
      menuClassName,
      menuDataQa,
      wrapClassName,
      wrapFocusClassName,
      dataQa
    } = this.props

    const menuWidth = syncWidth || menuMinWidth || null
    const isMenuRendered = showMenu || doSyncWidth || doSyncMinWidth
    const isMenuHidden = (doSyncWidth || doSyncMinWidth) && !showMenu

    return (
      <div
        ref={this._setWrap}
        style={{
          minWidth: syncWidth != null ? `${syncWidth}px` : null,
        }}
        className={classNames("wrap-drop-down-menu", wrapClassName, {
          [wrapFocusClassName]: showMenu,
        })}
        data-qa={dataQa}
        tabIndex={0}
        role="listbox"
        aria-haspopup="listbox"
        aria-expanded={isMenuRendered}
        aria-labelledby={this.props.listId}
        onBlur={this.onBlurHandler}
        onKeyDown={(event) => {
          const { keyCode } = event

          if (keyCode === 38 || keyCode === 40) {
            event.preventDefault()
            this.arrowHandler(keyCode)
          }

          if (keyCode === 13) {
            if (!isMenuRendered) {
              this.handleShowMenu()
            } else {
              this.enterHandler()
            }
          }

          if (keyCode === 27) this.handleShowMenu(false)
        }}>
        <div className={styles.hiddenDescribeContainer} id={this.props.listId}>
          {this.props.ariaLabelledBy}
        </div>
        {getButton(this.props, this.handleShowMenu)}
        {isMenuRendered ? (
          <div
            ref={this._setMenu}
            style={{
              left: menuOffsetLeft != null ? `${menuOffsetLeft}px` : null,
              minWidth: `${menuWidth}px`,
              top: menuOffsetTop != null ? `${menuOffsetTop}px` : null,
            }}
            className={classNames(styles.menu, menuClassName, {
              ["ps"]: !hasSubMenu,
              [styles.hiddenMenu]: isMenuHidden,
              [styles.hasSubMenu]: hasSubMenu,
              [styles.menuTopAligned]: doAlignMenu && doAlignMenuTop,
            })}
            data-qa={menuDataQa}>
            {React.Children.map(children, (element) => {
              return (
                element &&
                React.cloneElement(element, {
                  elementId: `${this.props.listId}${element.key}`,
                  hoverClassName: element.key === currentItem && "hovered",
                  onClick: (event) => {
                    element.props.onClick && element.props.onClick(event)

                    if (event.defaultPrevented) {
                      return
                    }

                    this.handleShowMenu()
                  },
                  ref: element.key === currentItem && this.itemReference,
                  role: "option",
                  selected: element.key === currentItem,
                })
              )
            })}
          </div>
        ) : null}
      </div>
    )
  }
}

export default DropDownMenu

DropDownMenu.propTypes = {
  className: PropTypes.string,
  doAlignMenu: PropTypes.bool,
  doAlignMenuRight: PropTypes.bool,
  doAlignMenuTop: PropTypes.bool,
  doDetectResize: PropTypes.bool,
  doSyncMinWidth: PropTypes.bool,
  doSyncWidth: PropTypes.bool,
  hasSubMenu: PropTypes.bool,
  hidePerfectScrollbar: PropTypes.bool,
  wrapClassName: PropTypes.string,
  wrapFocusClassName: PropTypes.string,
}

DropDownMenu.defaultProps = {
  buttonText: "",
  doAlignMenu: true,
  iconDataQa: "",
}
